import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import i18n from '../i18n';

interface User {
  id: string;
  email: string;
  fullName: string;
  profileUrl?: string;
  isApiUser: boolean;
  residences: Array<{
    customerId: string;
    block: string;
    name: string;
    meterTypes: string[];
    commercialServiceName?: string;
    residenceName?: string;
  }>;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
}

type ApiUserData = {
  email?: string;
  full_name?: string;
  profile_url?: string;
  role?: string;
  units?: Array<{
    customer_id: string;
    block: string;
    name: string;
    meter_types: string[];
    commercial_service_name?: string;
    residence_name?: string;
  }>;
};

function buildUser(data: ApiUserData): User {
  return {
    id: '',
    email: data.email || '',
    fullName: data.full_name || data.email || '',
    profileUrl: data.profile_url || '/images/default-avatar.png',
    isApiUser: data.role === 'API',
    residences: (data.units || []).map((unit) => ({
      customerId: unit.customer_id,
      block: unit.block,
      name: unit.name,
      meterTypes: unit.meter_types || [],
      commercialServiceName: unit.commercial_service_name,
      residenceName: unit.residence_name || unit.name,
    })),
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('oauth') === 'success') {
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      try {
        const response = await api.get('/condomino/me');
        if (response.data.success) {
          setUser(buildUser(response.data.user || {}));
          setToken('session');
        }
      } catch {
        // No valid session — user stays null
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.post('/condomino/entrar', {
        user: { email, password },
      });

      if (response.status === 200 && response.data.success) {
        setUser(buildUser(response.data.user || {}));
        setToken('session');
        return { success: true };
      } else {
        return { success: false, error: response.data.error || i18n.t('views.devise.sessions.invalid', 'Usuário ou senha inválidos') };
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorResponse = (err as { response?: { data?: { error?: string } } }).response;
      return { success: false, error: errorResponse?.data?.error || i18n.t('views.general.connection_error', 'Erro de conexão.') };
    }
  };

  const logout = async () => {
    try {
      await api.delete('/condomino/sair').catch(() => {});
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('current_residence_id');
    }
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...updates });
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isAuthenticated: !!token && !!user,
      isLoading,
      login,
      logout,
      updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
